import React from 'react';
import styled from '@emotion/styled';
import { theme } from 'common/theme';
import Icon from 'common/components/Icon';
import { useClickOutside } from 'common/hooks/useClickOutside';

const DropdownContainer = styled.div`
  position: relative;
  flex: 1;
`;

const DropdownButton = styled.button<{ isOpen: boolean }>`
  width: 100%;
  padding: ${theme.space(1.5)};
  height: 40px;
  background: white;
  border: 1px solid ${theme.colors.borderGrey.toString()};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: ${props => (props.isOpen ? theme.v2.colours.gray900 : theme.v2.colours.gray500)};
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    border-color: ${theme.v2.colours.latana};
  }

  svg {
    transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
    transition: transform 0.2s ease;
  }
`;

const DropdownList = styled.div`
  position: fixed;
  z-index: 99999;
  background: white;
  border: 1px solid ${theme.colors.borderGrey.toString()};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  min-width: 300px;
`;

const DropdownItem = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: ${theme.space(1.5)};
  background: ${props => (props.isSelected ? theme.v2.colours.gray50 : 'white')};
  border: none;
  text-align: left;
  cursor: pointer;
  color: ${theme.v2.colours.gray900};
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.v2.colours.gray50};
  }
`;

interface Option {
  value: string;
  label: string;
}

interface SimpleDropdownProps {
  options: Option[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
}

const SimpleDropdown: React.FC<SimpleDropdownProps> = ({ options, value, onChange, placeholder = 'Select an option' }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const selectedOption = options.find(option => option.value === value);

  const handleSelect = (option: Option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  const positionDropdown = React.useCallback((listElement: HTMLDivElement) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      listElement.style.top = `${rect.bottom + 4}px`;
      listElement.style.left = `${rect.left}px`;
      listElement.style.width = `${rect.width}px`;
    }
  }, []);

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton ref={buttonRef} type='button' onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
        <span>{selectedOption?.label || placeholder}</span>
        <Icon icon='arrow-chevron-down' size={16} />
      </DropdownButton>

      {isOpen && (
        <DropdownList ref={el => el && positionDropdown(el)}>
          {options.map(option => (
            <DropdownItem key={option.value} onClick={() => handleSelect(option)} isSelected={option.value === value} type='button'>
              {option.label}
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};

export default SimpleDropdown;
